/**
 * Service for communicating with Brønnøysundregistrene Open API
 * Documentation: https://data.brreg.no/enhetsregisteret/api/dokumentasjon/index.html
 * 
 * NOTE: The Open API allows CORS requests from browsers.
 * No API key needed for basic entity search.
 */

const BASE_URL = 'https://data.brreg.no/enhetsregisteret/api';

export const normalizeOrgNumber = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '');
};

export const getCanonicalOrgNumber = (input) => {
  if (input === null || input === undefined) return '';

  const value = typeof input === 'object'
    ? input.org_number ||
      input.orgnr ||
      input.organization_number ||
      input.organizationNumber ||
      input.organisasjonsnummer ||
      input.orgNumber
    : input;

  return normalizeOrgNumber(value);
};

export const validateOrgNumber = (value) => {
  const normalized = normalizeOrgNumber(value);

  if (normalized.length !== 9) {
    return {
      valid: false,
      normalized,
      error: 'Organisasjonsnummer må bestå av nøyaktig 9 sifre.'
    };
  }

  const digits = normalized.split('').map(Number);
  const weights = [3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((total, weight, index) => {
    return total + digits[index] * weight;
  }, 0);
  const controlDigit = 11 - (sum % 11);

  if (controlDigit === 10 || digits[8] !== (controlDigit === 11 ? 0 : controlDigit)) {
    return {
      valid: false,
      normalized,
      error: 'Organisasjonsnummeret har ugyldig kontrollsiffer.'
    };
  }

  return {
    valid: true,
    normalized,
    error: null
  };
};

export const createBrregError = (error, fallbackMessage = 'En ukjent feil oppstod mot BRREG.') => {
  const status = error?.status ?? error?.response?.status ?? null;

  if (error?.code === 'INVALID_ORG_NUMBER') {
    return {
      code: 'INVALID_ORG_NUMBER',
      message: error.message || 'Ugyldig organisasjonsnummer.',
      status: null
    };
  }

  if (status === 404) {
    return {
      code: 'NOT_FOUND',
      message: 'Fant ikke virksomheten i Brønnøysundregistrene.',
      status
    };
  }

  if (status === 429) {
    return {
      code: 'RATE_LIMITED',
      message: 'For mange forespørsler til Brønnøysundregistrene. Prøv igjen senere.',
      status
    };
  }

  if (error?.name === 'TypeError' || error?.code === 'NETWORK_ERROR') {
    return {
      code: 'NETWORK_ERROR',
      message: 'Kunne ikke kontakte Brønnøysundregistrene. Sjekk internettforbindelsen.',
      status: null
    };
  }

  return {
    code: error?.code || 'UNKNOWN_ERROR',
    message: error?.message || fallbackMessage,
    status
  };
};

const hasSearchValue = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return false;
  return true;
};

const firstSearchValue = (...values) => values.find(hasSearchValue);

const appendSearchParam = (params, key, value) => {
  if (!hasSearchValue(value)) return;

  if (Array.isArray(value)) {
    value.filter(hasSearchValue).forEach(item => params.append(key, String(item)));
    return;
  }

  params.append(key, String(value));
};

const appendBooleanSearchParam = (params, key, value) => {
  if (!hasSearchValue(value)) return;

  if (typeof value === 'boolean') {
    params.append(key, String(value));
  } else if (value === 'true' || value === 'false') {
    params.append(key, value);
  }
};

export const buildSearchParams = (filters = {}, page = 0, size = 20) => {
  const params = new URLSearchParams();
  const source = filters || {};
  params.append('page', page);
  params.append('size', size);

  // Default filters
  if (source.aktiv === true || source.active === true) {
    params.append('konkurs', 'false');
    params.append('underAvvikling', 'false');
    params.append('underTvangsavviklingEllerTvangsopplosning', 'false');
  } else {
    appendBooleanSearchParam(params, 'konkurs', source.konkurs);
    appendBooleanSearchParam(params, 'underAvvikling', source.underAvvikling);
    appendBooleanSearchParam(
      params,
      'underTvangsavviklingEllerTvangsopplosning',
      source.underTvangsavviklingEllerTvangsopplosning
    );
  }

  // Geo filters
  appendSearchParam(
    params,
    'kommunenummer',
    firstSearchValue(
      source.kommunenummer,
      source.municipalityNumber,
      source.municipality,
      source.kommune
    )
  );

  // Næringskode (NACE)
  appendSearchParam(
    params,
    'naeringskode',
    firstSearchValue(source.naeringskode, source.naceCode, source.nace)
  );

  // Orgform
  appendSearchParam(
    params,
    'organisasjonsform',
    firstSearchValue(
      source.organisasjonsform,
      source.organizationForm,
      source.orgform
    )
  );

  // Name search
  appendSearchParam(
    params,
    'navn',
    firstSearchValue(source.navn, source.companyName, source.name)
  );

  appendSearchParam(
    params,
    'organisasjonsnummer',
    firstSearchValue(
      source.organisasjonsnummer,
      source.organization_number,
      source.organizationNumber,
      source.org_number,
      source.orgnr,
      source.orgNumber
    )
  );

  appendSearchParam(
    params,
    'fraAntallAnsatte',
    firstSearchValue(source.fraAntallAnsatte, source.fromEmployees)
  );

  appendSearchParam(
    params,
    'tilAntallAnsatte',
    firstSearchValue(source.tilAntallAnsatte, source.toEmployees)
  );

  appendBooleanSearchParam(
    params,
    'registrertIMvaregisteret',
    firstSearchValue(
      source.registrertIMvaregisteret,
      source.mva,
      source.isVatRegistered
    )
  );

  appendBooleanSearchParam(
    params,
    'registrertIForetaksregisteret',
    firstSearchValue(
      source.registrertIForetaksregisteret,
      source.foretaksregisteret,
      source.isRegisteredInBusinessRegister
    )
  );

  appendSearchParam(params, 'stiftelsesdato', source.stiftelsesdato);
  appendSearchParam(
    params,
    'registreringsdatoEnhetsregisteret',
    source.registreringsdatoEnhetsregisteret
  );
  appendSearchParam(params, 'sort', firstSearchValue(source.sort, source.sortering));

  appendSearchParam(
    params,
    'forretningsadresse.kommunenummer',
    source['forretningsadresse.kommunenummer']
  );
  appendSearchParam(
    params,
    'postadresse.kommunenummer',
    source['postadresse.kommunenummer']
  );
  appendSearchParam(
    params,
    'forretningsadresse.postnummer',
    source['forretningsadresse.postnummer']
  );
  appendSearchParam(
    params,
    'postadresse.postnummer',
    source['postadresse.postnummer']
  );

  return params;
};

const getFirstValue = (value) => {
  if (Array.isArray(value)) return value[0] || '';
  return value || '';
};

export const normalizeEntity = (entity) => {
  const source = entity || {};
  const address = source.forretningsadresse || {};
  const organizationForm = source.organisasjonsform || {};
  const industry = getFirstValue(source.naeringskode1 || source.naeringskode);
  const industryCode = typeof industry === 'string'
    ? industry
    : industry?.kode || industry?.code || '';
  const industryDescription = typeof industry === 'string'
    ? ''
    : industry?.beskrivelse || industry?.description || '';

  const bankruptcy = Boolean(source.konkurs);
  const underLiquidation = Boolean(source.underAvvikling);
  const underForcedLiquidation = Boolean(
    source.underTvangsavvikling ||
    source.underTvangsavviklingEllerTvangsopplosning
  );
  const deleted = Boolean(source.slettet);

  let brregStatus = source.brreg_status || source.status || 'Aktiv';
  if (deleted) brregStatus = 'Slettet';
  else if (bankruptcy) brregStatus = 'Konkurs';
  else if (underForcedLiquidation) brregStatus = 'Under tvangsavvikling';
  else if (underLiquidation) brregStatus = 'Under avvikling';

  const orgnr = getCanonicalOrgNumber(source);
  const name = source.navn || source.name || '';
  const organizationFormCode =
    organizationForm.kode ||
    source.organisasjonsformkode ||
    source.organization_form_code ||
    '';
  const organizationFormDescription =
    organizationForm.beskrivelse ||
    source.organisasjonsformBeskrivelse ||
    source.organization_form_description ||
    '';
  const municipality =
    address.kommune ||
    source.municipality ||
    source.kommune ||
    '';
  const municipalityNumber =
    address.kommunenummer ||
    source.municipality_number ||
    source.municipalityNumber ||
    source.kommunenummer ||
    '';
  const addressValue = getFirstValue(address.adresse) || source.address || '';
  const employeesCount =
    Number.isInteger(source.antallAnsatte)
      ? source.antallAnsatte
      : Number.isInteger(source.employees_count)
        ? source.employees_count
        : null;
  const website = source.hjemmeside || source.website || '';
  const county = address.fylke || source.county || source.fylke || '';

  return {
    org_number: orgnr,
    orgnr,
    name,
    navn: name,
    organization_form_code: organizationFormCode,
    organization_form_description: organizationFormDescription,
    organization_form: organizationFormDescription || organizationFormCode,
    organisasjonsform: organizationFormDescription || organizationFormCode,
    industry_code: industryCode,
    industry_description: industryDescription,
    website,
    hjemmeside: website,
    address: addressValue,
    postal: addressValue,
    postal_code:
      address.postnummer ||
      address.postal_code ||
      source.postal_code ||
      '',
    city: address.poststed || address.city || source.city || '',
    municipality,
    kommune: municipality,
    municipality_number: municipalityNumber,
    kommunenummer: municipalityNumber,
    county,
    fylke: county,
    employees_count: employeesCount,
    antallAnsatte: employeesCount,
    bankruptcy,
    konkurs: bankruptcy,
    under_liquidation: underLiquidation,
    underAvvikling: underLiquidation,
    under_forced_liquidation: underForcedLiquidation,
    underTvangsavvikling: underForcedLiquidation,
    deleted,
    brreg_status: brregStatus,
    status: brregStatus,
    brreg_registered_at:
      source.registrertIEnhetsregisteret ||
      source.registreringsdatoEnhetsregisteret ||
      source.brreg_registered_at ||
      null,
    brreg_last_synced_at: source.brreg_last_synced_at || new Date().toISOString(),
    brreg_raw_data: source.brreg_raw_data || source,
    raw: source.brreg_raw_data || source
  };
};

export const mapEntityToCompany = (entity) => {
  const normalized = entity?.org_number !== undefined &&
    entity?.organization_form_code !== undefined
    ? entity
    : normalizeEntity(entity);

  return {
    org_number: getCanonicalOrgNumber(normalized),
    name: normalized.name || '',
    industry: normalized.industry_description || normalized.industry_code || '',
    website: normalized.website || '',
    city: normalized.city || '',
    address: normalized.address || '',
    postal_code: normalized.postal_code || '',
    country: 'NO',
    company_type:
      normalized.organization_form_description ||
      normalized.organization_form_code ||
      '',
    nace_code: normalized.industry_code || '',
    nace_description: normalized.industry_description || '',
    employees_count: Number.isInteger(normalized.employees_count)
      ? normalized.employees_count
      : null,
    brreg_status: normalized.brreg_status || '',
    brreg_registered_at: normalized.brreg_registered_at || null,
    brreg_last_synced_at:
      normalized.brreg_last_synced_at || new Date().toISOString(),
    brreg_source: 'brreg',
    brreg_raw_data: normalized.brreg_raw_data || null
  };
};

export const BrregService = {
  /**
   * Test connection to API
   */
  checkConnection: async () => {
    try {
      const start = Date.now();
      const response = await fetch(`${BASE_URL}/enheter?size=1`);
      const end = Date.now();
      if (response.ok) {
        return { status: 'OK', latency: end - start };
      }
      return { status: 'ERROR', message: response.statusText };
    } catch (error) {
      return { status: 'ERROR', message: error.message };
    }
  },

  /**
   * Search for entities (Enheter)
   * @param {Object} filters - Query builder filters
   * @param {Number} page - Page number (0-indexed)
   * @param {Number} size - Page size
   */
  searchEnheter: async (filters, page = 0, size = 20) => {
    const params = buildSearchParams(filters, page, size);
    const query = params.toString();

    try {
      const response = await fetch(`${BASE_URL}/enheter?${query}`);
      
      if (!response.ok) {
        let message = response.statusText;

        try {
          const payload = await response.clone().json();
          message = payload?.message || payload?.detail || message;
        } catch {
          // BRREG may return a non-JSON error response.
        }

        const brregError = createBrregError(
          {
            status: response.status,
            message: message || `BRREG API Error: ${response.status}`
          },
          'BRREG svarte med en feil.'
        );

        return {
          total: 0,
          totalElements: null,
          returnedCount: 0,
          previewCount: 0,
          data: [],
          error: brregError.message,
          errorInfo: {
            status: brregError.status,
            message: brregError.message,
            query
          },
          status: brregError.status,
          query
        };
      }
      
      const data = await response.json();
      const entities = data._embedded?.enheter || [];
      const totalElements = data.page?.totalElements;
      const returnedCount = entities.length;
      const hasTotalElements = Number.isFinite(totalElements);
      const previewCount = hasTotalElements
        ? totalElements
        : returnedCount;
      
      return {
        total: previewCount,
        totalElements: hasTotalElements ? totalElements : null,
        returnedCount,
        previewCount,
        countIsFallback: !hasTotalElements && returnedCount > 0,
        data: entities,
        error: null,
        errorInfo: null,
        status: response.status,
        query
      };
    } catch (error) {
      console.error("BRREG Fetch Error:", error);
      // Fallback/Mock data if API fails (common in restricted envs)
      const brregError = createBrregError(
        error,
        "Kunne ikke kontakte Brønnøysundregistrene. Sjekk internettforbindelsen."
      );

      return {
        total: 0,
        totalElements: null,
        returnedCount: 0,
        previewCount: 0,
        data: [],
        error: brregError.message,
        errorInfo: {
          status: brregError.status,
          message: brregError.message,
          query
        },
        status: brregError.status,
        query
      };
    }
  },

  /**
   * Lookup a single entity by organisation number.
   */
  getCompanyByOrgNumber: async (orgNumber) => {
    const validation = validateOrgNumber(orgNumber);

    if (!validation.valid) {
      const error = createBrregError({
        code: 'INVALID_ORG_NUMBER',
        message: validation.error
      });

      return {
        success: false,
        data: null,
        raw: null,
        error: error.message,
        status: error.status
      };
    }

    try {
      const response = await fetch(
        `${BASE_URL}/enheter/${encodeURIComponent(validation.normalized)}`
      );

      if (!response.ok) {
        throw { status: response.status, message: response.statusText };
      }

      const raw = await response.json();

      return {
        success: true,
        data: normalizeEntity(raw),
        raw,
        error: null,
        status: response.status
      };
    } catch (error) {
      const brregError = createBrregError(
        error,
        'Kunne ikke hente virksomheten fra Brønnøysundregistrene.'
      );

      return {
        success: false,
        data: null,
        raw: null,
        error: brregError.message,
        status: brregError.status
      };
    }
  },

  /**
   * Lookup roles for an entity 
   * NOTE: The public API does NOT provide roles (Daglig leder/Styre). 
   * This usually requires 'Roller' API which needs authentication/scopes or 3rd party.
   * We return an empty list until authenticated role data is available.
   */
  getRoles: async () => {
    return [];
  },

  normalizeOrgNumber,
  getCanonicalOrgNumber,
  validateOrgNumber,
  normalizeEntity,
  mapEntityToCompany,
  buildSearchParams,
  createBrregError
};

export const searchEnheter = (...args) => BrregService.searchEnheter(...args);